import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from '../../components/Pagination.tsx';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';

const BankDetails = () => {
    const { themeName } = useTheme();
    const currentTheme = getTheme(themeName);
    const [bankDetails, setBankDetails] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        id: "",
        bank_name: "",
        account_holder: "",
        account_number: "",
        ifsc_code: "",
        branch_name: "",
        is_default: false
    });
    const [form, setForm] = useState({
        bank_name: "",
        account_holder: "",
        account_number: "",
        ifsc_code: "",
        branch_name: ""
    });
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(6);

    const rawData = localStorage.getItem('user');
    const cred = rawData ? JSON.parse(rawData) : null;
    const token = cred?.access;
    const userId = cred?.user?.id;

    const fetchBankDetails = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_DB_URI}/users/bank/`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            setBankDetails(response.data);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchBankDetails();
    }, []);

    // Pagination calculations
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedBankDetails = bankDetails.slice(startIndex, endIndex);
    const totalPages = Math.ceil(bankDetails.length / rowsPerPage);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const handleAddBank = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        formData.append("user", userId);
        formData.append("bank_name", form.bank_name);
        formData.append("account_holder", form.account_holder);
        formData.append("account_number", form.account_number);
        formData.append("ifsc_code", form.ifsc_code);
        formData.append("branch_name", form.branch_name);

        try {
            await axios.post(`${process.env.REACT_APP_DB_URI}/users/bank/`, formData, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            setShowModal(false);
            setForm({
                bank_name: "",
                account_holder: "",
                account_number: "",
                ifsc_code: "",
                branch_name: ""
            });
            fetchBankDetails();
        } catch (err) {
            alert("Failed to add bank details.");
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (bank: any) => {
        setEditForm({
            id: bank.id,
            bank_name: bank.bank_name,
            account_holder: bank.account_holder,
            account_number: bank.account_number,
            ifsc_code: bank.ifsc_code,
            branch_name: bank.branch_name,
            is_default: bank.is_default
        });
        setShowEditModal(true);
    };

    const handleUpdateBank = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        formData.append("user", userId);
        formData.append("bank_name", editForm.bank_name);
        formData.append("account_holder", editForm.account_holder);
        formData.append("account_number", editForm.account_number);
        formData.append("ifsc_code", editForm.ifsc_code);
        formData.append("branch_name", editForm.branch_name);

        try {
            await axios.patch(`${process.env.REACT_APP_DB_URI}/users/bank/${editForm.id}/`, formData, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            setShowEditModal(false);
            fetchBankDetails();
        } catch (err) {
            alert("Failed to update bank details.");
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBank = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this bank detail?")) return;
        setLoading(true);
        try {
            await axios.delete(`${process.env.REACT_APP_DB_URI}/users/bank/${id}/`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            fetchBankDetails();
        } catch (err) {
            alert("Failed to delete bank details.");
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAtivate = async (id: string) => {
        try {
            await axios.post(`${process.env.REACT_APP_DB_URI}/users/bank/${id}/activate/`, {}, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            fetchBankDetails();
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div className="h-[calc(100vh-190px)]">
            <div className="flex justify-end items-center mb-1">
                {/* <h2 className="text-2xl font-bold text-teal-700">Bank Details</h2> */}
                <button
                    style={{ backgroundColor: currentTheme.primary, color: currentTheme.surface, padding: '8px 16px', borderRadius: '4px' }}
                    onClick={() => setShowModal(true)}
                >
                    Create New Bank
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-auto max-h-full custom-scrollbar">
                {paginatedBankDetails.length === 0 ? (
                    <div style={{ color: currentTheme.textSecondary }}>No bank details found.</div>
                ) : (
                    paginatedBankDetails.map((bank) => (
                        <div key={bank.id} style={{ backgroundColor: currentTheme.surface, borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: '16px', border: `1px solid ${currentTheme.primary}20`, position: 'relative' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ fontWeight: '600', fontSize: '18px', color: currentTheme.primary }}>{bank.account_holder}</div>
                                {bank.is_default && (
                                    <span style={{ padding: '4px 8px', backgroundColor: currentTheme.success + '33', color: currentTheme.success, borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>Default</span>
                                )}
                                {
                                    !bank?.is_default &&
                                    <button onClick={() => handleAtivate(bank?.id)} style={{ backgroundColor: currentTheme.secondary + '33', fontSize: '12px', borderRadius: '6px', padding: '4px 8px', color: currentTheme.secondary }}>activate</button>
                                }
                                <div style={{ display: 'flex', gap: '8px', position: 'absolute', top: '12px', right: '12px' }}>
                                    <button
                                        style={{ color: currentTheme.textSecondary }}
                                        onClick={() => openEditModal(bank)}
                                        title="Edit"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        style={{ color: currentTheme.textSecondary }}
                                        onClick={() => handleDeleteBank(bank.id)}
                                        title="Delete"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                            <div style={{ fontSize: '14px', color: currentTheme.text, marginBottom: '4px' }}>
                                <span style={{ fontWeight: '500' }}>Bank:</span> {bank.bank_name}
                            </div>
                            <div style={{ fontSize: '14px', color: currentTheme.text, marginBottom: '4px' }}>
                                <span style={{ fontWeight: '500' }}>Account No:</span> {bank.account_number}
                            </div>
                            <div style={{ fontSize: '14px', color: currentTheme.text, marginBottom: '4px' }}>
                                <span style={{ fontWeight: '500' }}>IFSC:</span> {bank.ifsc_code}
                            </div>
                            <div style={{ fontSize: '14px', color: currentTheme.text }}>
                                <span style={{ fontWeight: '500' }}>Branch:</span> {bank.branch_name}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination Controls */}
            {bankDetails.length > 0 && (
                <div className="flex items-center justify-between px-2 py-3 mt-2">
                    <div className="flex items-center gap-3">
                        <span className="text-sm" style={{ color: currentTheme.textSecondary }}>Items per page:</span>
                        <select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
                            style={{
                                backgroundColor: currentTheme.surface,
                                color: currentTheme.text,
                                borderColor: currentTheme.border
                            }}
                        >
                            <option value={6}>6</option>
                            <option value={12}>12</option>
                            <option value={24}>24</option>
                            <option value={48}>48</option>
                        </select>
                        <span className="text-sm" style={{ color: currentTheme.textMuted }}>
                            Showing {bankDetails.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, bankDetails.length)} of {bankDetails.length} banks
                        </span>
                    </div>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            )}

            {/* Modal for Add New Bank */}
            {showModal && (
                <div className="fixed inset-0 z-[991] flex items-center justify-center bg-black bg-opacity-40">
                    <div style={{ backgroundColor: currentTheme.surface, borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', width: '100%', maxWidth: '28rem', padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: currentTheme.primary }}>Add New Bank</h3>
                            <button
                                style={{ color: currentTheme.textSecondary, fontSize: '24px', fontWeight: 'bold' }}
                                onClick={() => setShowModal(false)}
                                disabled={loading}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleAddBank} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Bank Name</label>
                                <input
                                    type="text"
                                    name="bank_name"
                                    value={form.bank_name}
                                    onChange={handleInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Account Holder</label>
                                <input
                                    type="text"
                                    name="account_holder"
                                    value={form.account_holder}
                                    onChange={handleInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Account Number</label>
                                <input
                                    type="text"
                                    name="account_number"
                                    value={form.account_number}
                                    onChange={handleInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>IFSC Code</label>
                                <input
                                    type="text"
                                    name="ifsc_code"
                                    value={form.ifsc_code}
                                    onChange={handleInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Branch Name</label>
                                <input
                                    type="text"
                                    name="branch_name"
                                    value={form.branch_name}
                                    onChange={handleInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <button
                                type="submit"
                                style={{ width: '100%', backgroundColor: currentTheme.primary, color: currentTheme.surface, padding: '8px', borderRadius: '4px' }}
                                disabled={loading}
                            >
                                {loading ? "Adding..." : "Add Bank"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal for Edit Bank */}
            {showEditModal && (
                <div className="fixed inset-0 z-[992] flex items-center justify-center bg-black bg-opacity-40">
                    <div style={{ backgroundColor: currentTheme.surface, borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', width: '100%', maxWidth: '28rem', padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: currentTheme.primary }}>Update Bank</h3>
                            <button
                                style={{ color: currentTheme.textSecondary, fontSize: '24px', fontWeight: 'bold' }}
                                onClick={() => setShowEditModal(false)}
                                disabled={loading}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleUpdateBank} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Bank Name</label>
                                <input
                                    type="text"
                                    name="bank_name"
                                    value={editForm.bank_name}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Account Holder</label>
                                <input
                                    type="text"
                                    name="account_holder"
                                    value={editForm.account_holder}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Account Number</label>
                                <input
                                    type="text"
                                    name="account_number"
                                    value={editForm.account_number}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>IFSC Code</label>
                                <input
                                    type="text"
                                    name="ifsc_code"
                                    value={editForm.ifsc_code}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: currentTheme.text, marginBottom: '4px' }}>Branch Name</label>
                                <input
                                    type="text"
                                    name="branch_name"
                                    value={editForm.branch_name}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ border: `1px solid ${currentTheme.border}`, padding: '8px', borderRadius: '4px', width: '100%' }}
                                />
                            </div>
                            <button
                                type="submit"
                                style={{ width: '100%', backgroundColor: currentTheme.primary, color: currentTheme.surface, padding: '8px', borderRadius: '4px' }}
                                disabled={loading}
                            >
                                {loading ? "Updating..." : "Update Bank"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankDetails;